/* eslint-disable import/prefer-default-export */
import * as React from "react";
import {
  Accordion,
  AccordionV2,
  Button,
  Carousel,
  Heading,
  Icon,
} from "@components";
import classNames from "classnames";
import Head from "next/head";

import useVisibility from "../../../hooks/useVisibility";
import {
  addHeapBackToTopClicked,
  addHeapContentExpanded,
} from "../../services/elevar/events";
import { Media } from "../Media";
import { TextOverlayMedia, TextOverlayMediaProps } from "../TextOverlayMedia";

import selectors from "./selectors";

interface CollapsibleContentProps {
  content: React.ReactNode;
  media?: TextOverlayMediaProps[];
}
function CollapsibleContent({ content, media }: CollapsibleContentProps) {
  const shouldPaginate = media && media.length >= 3;
  const innerContent = media?.map((mediaItem, index) => (
    <TextOverlayMedia
      // eslint-disable-next-line react/no-array-index-key
      key={index}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...mediaItem}
      className={classNames({ "pb-16 lg:pb-0": shouldPaginate })}
    />
  ));

  return (
    <div
      className={classNames("flex flex-col gap-8", {
        "mb-10": !shouldPaginate,
      })}
      data-testid={selectors.collapsibleContent}
      data-paginated={shouldPaginate}
    >
      <div className="flex flex-col gap-4">{content}</div>
      {media && (
        <div data-testid={selectors.collapsibleMedia}>
          <Media
            greaterThanOrEqual="lg"
            displayStyle="grid"
            className="grid-cols-4 gap-4"
          >
            {innerContent}
          </Media>
          <Media lessThan="lg">
            <Carousel
              slidesPerView={3}
              autoplay={{ enabled: false }}
              spaceBetween={10}
              navigation={false}
              loop={false}
              pagination={
                shouldPaginate
                  ? { type: "bullets", overlay: false }
                  : { type: undefined }
              }
            >
              {innerContent}
            </Carousel>
          </Media>
        </div>
      )}
    </div>
  );
}

// TODO: i18n
export const collapsedButtonText = "See All";
export const expandedButtonText = "Show Less";

function RenderAccordion({
  layout,
  name,
  id,
  limit,
  collapsibles,
  showBackToTop,
}: Pick<
  CollapsibleSectionProps,
  "limit" | "layout" | "name" | "collapsibles" | "id" | "showBackToTop"
>) {
  const sectionName = "faqSection";

  const { isFullCollapsibleListVisible, toggleFullCollapsibleList } =
    useVisibility("FullCollapsibleList", false, {
      onHide: () =>
        addHeapContentExpanded(sectionName, expandedButtonText, false),
      onShow: () =>
        addHeapContentExpanded(sectionName, collapsedButtonText, true),
    });
  const visibleCollapsibles = isFullCollapsibleListVisible
    ? collapsibles
    : collapsibles.slice(0, limit);
  const buttonText = isFullCollapsibleListVisible
    ? expandedButtonText
    : collapsedButtonText;

  return layout === "legacy" ? (
    <Accordion
      name={sectionName}
      useHeadings
      trackPaneChanges
      items={collapsibles.map((collapsible) => ({
        id: collapsible.id,
        anchor: collapsible.anchor,
        title: collapsible.title,
        children: (
          <CollapsibleContent
            content={collapsible.copy}
            media={collapsible.media}
          />
        ),
      }))}
    />
  ) : (
    <>
      <AccordionV2
        name={sectionName}
        collapsible
        trackInteraction
        showItemLines
        items={visibleCollapsibles.map((collapsible, index) => ({
          id: collapsible.id,
          title: collapsible.title,
          wasInitiallyHidden: limit !== undefined && index >= limit,
          children: (
            <CollapsibleContent
              content={collapsible.copy}
              media={collapsible.media}
            />
          ),
        }))}
      />
      <div className="flex justify-between pt-6">
        <div className="flex space-x-3 text-xs text-slate">
          {limit && collapsibles.length > limit ? (
            <>
              <span>
                {/* TODO: i18n */}
                Showing{" "}
                {!isFullCollapsibleListVisible
                  ? limit
                  : collapsibles.length} of {collapsibles.length}
              </span>
              <Button
                data-testid={selectors.seeAll}
                // TODO: i18n
                title="See All"
                variant="underline"
                onClick={toggleFullCollapsibleList}
                className="mb-0 text-charcoal hover:text-black"
              >
                {buttonText}
              </Button>
            </>
          ) : null}
        </div>
        {showBackToTop ? (
          <Button
            data-testid={selectors.toTop}
            // TODO: i18n
            title="Back to Top"
            variant="unstyled"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              addHeapBackToTopClicked({
                name,
                id,
                location: "CollapsibleSection",
              });
            }}
            className="flex items-center text-charcoal hover:text-black"
          >
            {/* TODO: i18n */}
            Back to Top
            <Icon name="arrow-up" className="pl-2" />
          </Button>
        ) : null}
      </div>
    </>
  );
}

interface Collapsible {
  id: string;
  anchor?: string;
  title: string;
  copy: React.ReactNode;
  media?: TextOverlayMediaProps[];
  seoData?: { question: string; answer: string };
}

export interface CollapsibleSectionProps {
  name: string;
  id: string;
  title?: string | null;
  layout?: "legacy" | "modern";
  limit?: number;
  showBackToTop?: boolean;
  index?: number;
  adBanner: TextOverlayMediaProps | null;
  collapsibles: Collapsible[];
  shouldCollapse?: boolean;
  hideTitle?: boolean;
}

/**
 * Displays a Collapsible section with a section title and accordion for q&a
 */
export function CollapsibleSection({
  name,
  id,
  index,
  adBanner,
  collapsibles,
  shouldCollapse,
  hideTitle,
  layout = "legacy",
  limit,
  showBackToTop,
  title,
}: CollapsibleSectionProps) {
  const structuredDataArr = React.useMemo(
    () =>
      collapsibles
        .filter((collapsible) => collapsible.seoData)
        .map((collapsible) => ({
          "@context": "https://schema.org",
          "@type": "Question",
          name: collapsible.seoData?.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: collapsible.seoData?.answer,
          },
        })),
    [collapsibles]
  );

  return (
    <section id={id}>
      {Boolean(structuredDataArr.length) && (
        <Head>
          {structuredDataArr.map((item) => (
            <script
              id={`faq-seo-${item.name}`}
              key={item.name}
              type="application/ld+json"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
            />
          ))}
        </Head>
      )}
      {hideTitle ? (
        <div className="mt-8 lg:mt-16" />
      ) : (
        <>
          <h2
            className={classNames("pb-4 font-serif leading-tight text-mxl", {
              "mt-28": index !== 0 && layout !== "modern",
              "mt-0 lg:mt-28": index === 0,
              "mt-0": layout === "modern",
            })}
          >
            {title || name}
          </h2>
          <hr
            role="presentation"
            className="w-20 h-1 mb-4 bg-gray-800 border-b-4"
          />
        </>
      )}
      {shouldCollapse ? (
        <RenderAccordion
          name={title || name}
          id={id}
          layout={layout}
          limit={limit}
          collapsibles={collapsibles}
          showBackToTop={showBackToTop}
        />
      ) : (
        collapsibles.map((collapsible) => (
          <div key={collapsible.id} id={collapsible.anchor}>
            {adBanner && (
              <div
                data-testid={selectors.collapsibleSectionAd}
                className="mb-16 lg:mb-[50px] -mx-8 lg:mx-0"
              >
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <TextOverlayMedia {...adBanner} />
              </div>
            )}

            <Heading
              as={hideTitle ? "h5" : "h4"}
              className={classNames({
                "uppercase font-medium mb-2 !text-input": hideTitle,
              })}
              data-testid={selectors.nonAccordionHeading}
            >
              {collapsible.title}
            </Heading>

            <CollapsibleContent
              content={collapsible.copy}
              media={collapsible.media}
            />
          </div>
        ))
      )}
    </section>
  );
}
